import { api, LightningElement } from "lwc";
import { logger } from "@devforce/trailhead-client-logger";
import { getLocale } from "@devforce/trailhead-client-locale";
import debounce from "lodash/debounce";

import type {
  StandaloneSearchBox as SearchBox,
  StandaloneSearchBoxOptions as SearchBoxOptions,
  SearchEngine,
} from "@coveo/headless";

import {
  buildSearchEngine,
  buildStandaloneSearchBox as buildSearchBox,
} from "@coveo/headless/dist/browser/headless.js";

import { Listbox, ListboxState } from "./lib/listbox";

import { Completion, CoveoOptions, RecentSection, SearchEvent } from "./types";

export default class extends LightningElement {
  @api completions: Completion[] | null = [];
  @api defaultValue: string | null = null;
  @api placeholder: string = "Search";
  @api recents: RecentSection[] | null = null;
  @api suggestionsConfig: CoveoOptions | null = null;
  @api get value() {
    return (this.template.querySelector("tds-input")! as HTMLInputElement)
      .value;
  }

  private completionTerms: string | null = null;
  private setup = false;
  private coveoEngine: SearchEngine | null = null;
  private coveoController: SearchBox | null = null;
  private coveoSearchBoxOptions: SearchBoxOptions = {
    numberOfSuggestions: 5,
    highlightOptions: {
      exactMatchDelimiters: {
        open: '<span class="highlight">',
        close: "</span>",
      },
      notMatchDelimiters: {
        open: "",
        close: "",
      },
      correctionDelimiters: {
        open: '<span class="correction">',
        close: "</span>",
      },
    },
    redirectionUrl: "none",
  };

  private listbox = new Listbox({
    onChange: (state) => {
      this.listboxState = state;
    },
    onSelect: (index) => {
      let { completions, hasCompletions, hasRecents, recents } = this;
      if (hasCompletions) {
        if (this.coveoController) {
          this.setInputvalue(completions![index].value);
          this.coveoController.selectSuggestion(completions![index].value);
        }
        this.dispatchSearchEvent({
          type: "completion",
          value: completions![index],
        });
      } else if (hasRecents) {
        this.dispatchSearchEvent({
          type: "recent",
          value: recents!.flatMap((s) => s.items)[index],
        });
      }
    },
    onSubmit: () => {
      let { completionTerms } = this;
      if (!completionTerms) return;
      this.dispatchSearchEvent({ type: "input", value: completionTerms });
    },
    shadowRoot: this.template,
  });
  private listboxState: ListboxState | null = null;

  private get hasCompletions() {
    return this.completions && this.completions.length > 0;
  }

  private get hasRecents() {
    return this.recents && this.recents.length > 0;
  }

  private get recentsWithStyle() {
    return this.recents!.map((section) =>
      Object.assign({}, section, {
        items: section.items.map((item) =>
          Object.assign({}, item, {
            iconStyle: item.iconBackgroundColor
              ? `background-color: ${item.iconBackgroundColor};`
              : "",
          })
        ),
      })
    );
  }

  private dispatchSearchEvent(event: SearchEvent) {
    this.dispatchEvent(new CustomEvent("search", { detail: event }));
  }

  private onInput = (e: InputEvent) => {
    let { value } = e.currentTarget as HTMLInputElement;
    let terms = value.trim();
    this.completionTerms = terms;
    this.updateCoveoQueryText(terms);
    this.dispatchEvent(
      new CustomEvent("change", {
        detail: { value: terms },
      })
    );
  };

  private updateCoveoQueryText = debounce((query: string): void => {
    this.coveoController?.updateText(query);
  }, 300);

  private updateCompletions() {
    if (this.coveoController) {
      this.completions = this.coveoController.state.suggestions.map(
        (suggestion) =>
          Object.assign(
            {},
            {
              value: suggestion.rawValue,
              valueHighlighted: suggestion.highlightedValue,
            }
          )
      );
    }
  }

  private get showRecents() {
    let { completionTerms, hasCompletions, hasRecents, listboxState } = this;
    if (!listboxState) return false;
    if (completionTerms && completionTerms.length) return false;
    return listboxState.active && hasRecents && !hasCompletions;
  }

  private get showCompletions() {
    let { hasCompletions, listboxState } = this;
    if (!listboxState) return false;
    return listboxState.active && hasCompletions;
  }

  private setInputvalue(value: string): void {
    const inputField = this.template.querySelector(
      ".search-input__input"
    )! as HTMLInputElement;
    inputField.value = value;
  }

  private setupCoveoHeadlessController(): void {
    if (this.suggestionsConfig?.querySuggestionsEnabled) {
      if (
        !this.suggestionsConfig.accessToken ||
        !this.suggestionsConfig.organizationId
      ) {
        logger.warn(
          "Query Suggestions not enabled. No access token or organization ID."
        );
      } else {
        try {
          this.coveoEngine = buildSearchEngine({
            configuration: {
              organizationId: this.suggestionsConfig?.organizationId,
              accessToken: this.suggestionsConfig?.accessToken,
              search: {
                locale: getLocale(),
                searchHub: this.suggestionsConfig?.searchHub,
                pipeline: this.suggestionsConfig?.pipeline,
              },
            },
          });
          this.coveoController = buildSearchBox(this.coveoEngine, {
            options: this.coveoSearchBoxOptions,
          });
          this.coveoController?.subscribe(() => this.updateCompletions());
        } catch (e: unknown) {
          if (e instanceof Error) {
            logger.error(
              `Unable to instantiate Coveo search engine. Error "${e.name}" said: "${e.message}"`
            );
          }
        }
      }
    }
  }

  renderedCallback() {
    if (!this.setup) {
      this.setupCoveoHeadlessController();
      if (this.defaultValue != undefined) {
        this.setInputvalue(this.defaultValue);
        this.completionTerms = this.defaultValue;
        this.coveoController?.updateText(this.defaultValue);
      } else {
        this.coveoController?.updateText("");
      }
      this.setup = true;
    }
    this.listbox.renderedCallback();
  }

  disconnectedCallback() {
    this.listbox.disconnectedCallback();
  }
}
